import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export const BeforeAfterSlider = ({ before, after, label, testId }) => {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <div className="group" data-testid={testId}>
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        className="relative aspect-[3/4] overflow-hidden border border-[#E5E5E5] bg-white select-none cursor-ew-resize touch-none"
        data-testid={`${testId}-canvas`}
      >
        {/* After (base layer) */}
        <img
          src={after}
          alt={`${label} – Nachher`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        {/* Before (clipped to left via clip-path) */}
        <img
          src={before}
          alt={`${label} – Vorher`}
          draggable={false}
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />

        {/* Labels */}
        <span
          style={{ opacity: pos > 40 ? 1 : 0 }}
          className="absolute top-3 left-3 z-20 bg-[#1A1A1A]/70 text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full backdrop-blur-sm pointer-events-none transition-opacity duration-200"
        >
          Vorher
        </span>
        <span
          style={{ opacity: pos < 60 ? 1 : 0 }}
          className="absolute top-3 right-3 z-20 bg-[#3d7d83]/85 text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full backdrop-blur-sm pointer-events-none transition-opacity duration-200"
        >
          Nachher
        </span>

        {/* Divider + handle */}
        <div
          className="absolute inset-y-0 z-10 w-[2px] bg-white shadow-[0_0_8px_rgba(0,0,0,0.3)] pointer-events-none"
          style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-[0_2px_12px_rgba(0,0,0,0.25)] flex items-center justify-center text-[#3d7d83]">
            <MoveHorizontal size={20} />
          </div>
        </div>

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#1A1A1A]/75 to-transparent pointer-events-none">
          <p className="text-white font-playfair text-lg">{label}</p>
          <p className="text-white/70 text-xs tracking-wider uppercase">
            Ziehen zum Vergleichen
          </p>
        </div>
      </div>
    </div>
  );
};
